import type { ReactNode, RefObject } from "react"
import { useEffect, useState } from "react"
import { AnimatePresence, motion } from "motion/react"
import { IconArrowUpFill18 as ArrowUp } from "nucleo-ui-fill-18"
import {
	IconAtom2Outline18 as Atom,
	IconMicrophoneOutline18 as Microphone,
} from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/browser"
import { ShortcutSymbols } from "@productlane/lib/src/shortcut-symbols"

import { subtleSurfaceFocusRing, subtleSurfaceRing } from "../styles/surface"
import { Button, buttonVariants } from "./button"
import { CHAT_INPUT_IDLE_ICON_COLOR } from "./chat-input-icons"
import { IconButton } from "./icon-button"
import { Shortcut } from "./shortcut"
import { joinDictation, useSpeechInput } from "./use-speech-input"
import { VoiceInputButton } from "./voice-input-button"

interface AiChatInputProps {
	value?: string
	onChange?: (value: string) => void
	// Return false to keep the typed message (the submit was not handled).
	onSubmit: (message: string) => boolean | void
	placeholder?: string
	autoFocus?: boolean
	className?: string
	fixed?: boolean
	inputRef?: RefObject<HTMLInputElement | null>
	hideShortcut?: boolean
	variant?: "default" | "card"
	elevated?: boolean
	// The card variant rests on two rows. A launcher wants one.
	singleRow?: boolean
	// Adds a mic that dictates into the field. Off where the bar is a shortcut
	// target rather than a composer.
	voiceInput?: boolean
	// Adds a mic that hands off to a voice conversation elsewhere, like the widget chat.
	onVoice?: () => void
	voiceLabel?: string
	// Keeps the card's submit primary while empty, for a launcher that invites a first message.
	alwaysPrimarySubmit?: boolean
	// Sits before the field inside the card, like the agent's orb on the docs home.
	leading?: ReactNode
}

export const aiChatInputSurfaceClasses = (
	variant: "default" | "card",
	elevated = true,
	singleRow = false,
) =>
	cn(
		buttonVariants({ variant: "glass", size: "lg" }),
		"w-full",
		"max-w-full",
		"cursor-text",
		// A single card row insets the send button as far from the right as from top and bottom.
		variant === "card"
			? singleRow
				? "pl-3 pr-2.5"
				: "pl-3 pr-3"
			: "pl-3.5 pr-2",
		variant === "card"
			? [
					"rounded-lg",
					singleRow
						? "h-12 items-center py-0"
						: "h-20 flex-wrap content-between py-3",
					"bg-white dark:bg-gray-800",
					subtleSurfaceRing,
					"shadow-[inset_0_1px_0_rgba(255,255,255,0.9),0_1px_2px_rgba(15,17,21,0.05),0_8px_18px_-14px_rgba(15,17,21,0.35)]",
					"dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.06),0_1px_2px_rgba(0,0,0,0.5)]",
					"hover:bg-white dark:hover:bg-gray-800",
					subtleSurfaceFocusRing,
				]
			: [
					"rounded-full",
					"h-10",
					"sm:h-12",
					elevated
						? [
								"shadow-[0_2px_6px_rgba(0,0,0,0.06),0_10px_28px_-6px_rgba(0,0,0,0.16)]",
								"dark:shadow-[0_2px_6px_rgba(0,0,0,0.4),0_10px_28px_-6px_rgba(0,0,0,0.5)]",
								"transition-shadow duration-200 ease-out",
								"hover:shadow-[0_2px_8px_rgba(0,0,0,0.08),0_14px_36px_-6px_rgba(0,0,0,0.2)]",
								"dark:hover:shadow-[0_2px_8px_rgba(0,0,0,0.45),0_14px_36px_-6px_rgba(0,0,0,0.55)]",
							]
						: [
								"shadow-[0_1px_2px_rgba(0,0,0,0.04),0_4px_12px_-6px_rgba(0,0,0,0.1)]",
								"dark:shadow-[0_1px_2px_rgba(0,0,0,0.3),0_4px_12px_-6px_rgba(0,0,0,0.4)]",
							],
					"bg-white ring-1 ring-gray-900/10 dark:ring-white/10",
					"md:ring-black/10",
					"dark:bg-gray-850",
					"hover:bg-white dark:hover:bg-gray-800",
					"backdrop-blur-md",
					subtleSurfaceFocusRing,
				],
	)

// The submit button always renders; it only turns primary once there is something to send.
export const aiChatSubmitVariant = (hasValue: boolean) =>
	hasValue ? "default" : "outline"

export const AiChatInput = ({
	value: controlledValue,
	onChange,
	onSubmit,
	placeholder = "Ask anything...",
	autoFocus = false,
	className = "",
	fixed = false,
	inputRef,
	hideShortcut = false,
	variant = "default",
	elevated = true,
	singleRow = false,
	voiceInput = false,
	onVoice,
	voiceLabel = "Ask by voice",
	alwaysPrimarySubmit = false,
	leading,
}: AiChatInputProps) => {
	const [internalValue, setInternalValue] = useState("")
	const isControlled = controlledValue !== undefined
	const value = isControlled ? controlledValue : internalValue

	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "j") {
				e.preventDefault()
				inputRef?.current?.focus()
			}
		}

		window.addEventListener("keydown", handleKeyDown)
		return () => {
			window.removeEventListener("keydown", handleKeyDown)
		}
	}, [inputRef])

	const handleChange = (newValue: string) => {
		if (!isControlled) {
			setInternalValue(newValue)
		}
		onChange?.(newValue)
	}

	const speech = useSpeechInput({
		onTranscript: (text) => handleChange(joinDictation(value, text)),
	})

	const hasValue = value.trim().length > 0

	const handleSubmit = () => {
		if (!value.trim()) return
		speech.cancel()
		const handled = onSubmit(value.trim())
		if (handled === false) return
		if (!isControlled) {
			setInternalValue("")
		}
	}

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault()
			handleSubmit()
		}
	}

	const content = (
		<div
			className={cn(
				"relative",
				variant === "card" ? "rounded-lg" : "rounded-full",
				className,
			)}
		>
			<label
				className={cn(
					aiChatInputSurfaceClasses(variant, elevated, singleRow),
					className,
				)}
			>
				{variant === "card" && leading}
				{variant !== "card" && (
					<Atom
						className={cn(
							"text-[var(--portal-accent-color,var(--accent-color))]",
							"size-4 shrink-0 sm:size-[18px]",
						)}
					/>
				)}

				<input
					value={value}
					autoFocus={autoFocus}
					ref={inputRef}
					onChange={(e) => handleChange(e.target.value)}
					className={cn(
						"relative z-10 min-w-0 flex-grow bg-transparent tracking-tight text-gray-900 outline-none dark:text-gray-200",
						variant === "card"
							? cn(
									"text-[0.8rem] font-normal placeholder:text-gray-500 placeholder:opacity-90 dark:placeholder:text-gray-400",
									!singleRow && "w-full",
								)
							: "text-sm font-normal placeholder:text-[#8b8f96] dark:placeholder:text-gray-400",
					)}
					placeholder={placeholder}
					type="text"
					autoComplete="off"
					name="message"
					onKeyDown={handleKeyDown}
				/>
				{variant === "card" ? (
					<span className="relative z-10 ml-auto flex shrink-0 items-center gap-1">
						{voiceInput && (
							<VoiceInputButton
								speech={speech}
								className="h-6 w-6 sm:h-7 sm:w-7"
							/>
						)}
						{onVoice && (
							<Button
								type="button"
								variant="ghost"
								aria-label={voiceLabel}
								onClick={(e) => {
									e.preventDefault()
									onVoice()
								}}
								wrapperClassName="contents"
								className="h-[30px] w-[30px] shrink-0 cursor-pointer rounded-lg p-0"
							>
								<Microphone
									className={cn(
										"h-4 w-4 flex-none",
										CHAT_INPUT_IDLE_ICON_COLOR,
									)}
								/>
							</Button>
						)}
						<IconButton
							Icon={ArrowUp}
							onClick={handleSubmit}
							variant={
								alwaysPrimarySubmit || value.trim() ? "default" : "glass"
							}
							iconStrokeWidth={1.25}
							iconClassName="h-3.5 w-3.5 -translate-y-[0.5px]"
							className={cn(
								"shrink-0 cursor-pointer rounded-full transition-colors",
								!alwaysPrimarySubmit &&
									!value.trim() && [
										subtleSurfaceRing,
										"dark:bg-gray-750 dark:hover:bg-gray-700",
									],
							)}
						/>
					</span>
				) : (
					<>
						{voiceInput && (
							<VoiceInputButton
								speech={speech}
								className="relative z-10 h-6 w-6 shrink-0 sm:h-7 sm:w-7"
							/>
						)}
						<AnimatePresence>
							{!value && !hideShortcut && (
								<motion.div
									key="shortcut"
									initial={{ opacity: 0 }}
									animate={{ opacity: 1 }}
									exit={{ opacity: 0 }}
									className="relative z-10 hidden shrink-0 items-center gap-1 sm:flex"
								>
									<Shortcut className="mr-2">
										{ShortcutSymbols.CMD}
										{ShortcutSymbols.J}
									</Shortcut>
								</motion.div>
							)}
						</AnimatePresence>
						<Button
							onClick={handleSubmit}
							variant={aiChatSubmitVariant(hasValue)}
							wrapperClassName="contents"
							className="relative z-10 size-7 min-h-0 shrink-0 rounded-full p-0 transition-colors sm:size-8"
						>
							<ArrowUp className="size-3.5 flex-none -translate-y-[0.5px] sm:size-4" />
						</Button>
					</>
				)}
			</label>
		</div>
	)

	if (fixed) {
		return (
			<div
				className="fixed bottom-2.5 left-1/2 z-50 w-full -translate-x-1/2 px-14 sm:bottom-6 sm:max-w-[330px] sm:px-4"
				style={{
					paddingBottom: "var(--portal-keyboard-inset, 0px)",
				}}
			>
				{content}
			</div>
		)
	}

	return content
}
